import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Table } from "@tanstack/react-table";
import { Trash2Icon } from "lucide-react";
import { deleteEtpaOrdersAutoTraderDeleteEtpaOrdersDelete } from "@/client-admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";

interface DeleteEtpaOrdersDialogProps<TData> {
  orderIds: string[];
  selectedCount: number;
  table: Table<TData>;
}

export function DeleteEtpaOrdersDialog<TData>({ orderIds, selectedCount, table }: DeleteEtpaOrdersDialogProps<TData>) {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: deleteEtpaOrdersAutoTraderDeleteEtpaOrdersDelete,
    onSuccess: async (response) => {
      // Clear row selection
      table.resetRowSelection();

      await queryClient.refetchQueries({ queryKey: ["orders"] });
      await queryClient.refetchQueries({ queryKey: ["etpa-orders"] });

      toast({
        title: "Success",
        description: response.message,
      });
      setIsDialogOpen(false);
    },
    onError: (error) => {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to delete ETPA orders",
        variant: "destructive",
      });
    },
  });

  const handleDelete = async () => {
    await deleteMutation.mutateAsync({ requestBody: orderIds });
  };

  const isPending = deleteMutation.isPending;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setIsDialogOpen(true);
    } else if (!isPending) {
      setIsDialogOpen(false);
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="destructive" size="sm" className="h-8">
          <Trash2Icon className="mr-2 h-4 w-4" />
          Delete ({selectedCount})
        </Button>
      </DialogTrigger>
      <DialogContent onPointerDownOutside={(e) => isPending && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Delete ETPA Orders</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete {selectedCount} selected order{selectedCount !== 1 ? "s" : ""}? This action
            will cancel the order{selectedCount !== 1 ? "s" : ""} from ETPA.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={handleDelete} disabled={isPending} variant="destructive">
            {isPending ? "Deleting..." : `Delete ${selectedCount} Order${selectedCount !== 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
